import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUserProfile, loginUser, registerUser, logoutUser, completeFirstLogin, completeFirstLoginAuthenticated, configureAuthToken, configureAuthErrorHandler } from '../utils/api';
import analytics from '../services/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Inner provider that has access to navigation
const AuthProviderInner = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const navigate = useNavigate();

  // Handle auth errors (401) by logging out and redirecting
  const handleAuthError = useCallback(() => {
    // Clear auth state without using logout function to avoid circular dependency
    localStorage.removeItem('authToken');
    configureAuthToken(null);
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // Set up auth error handler
  useEffect(() => {
    configureAuthErrorHandler(handleAuthError);
    
    // Cleanup on unmount
    return () => {
      configureAuthErrorHandler(null);
    };
  }, [handleAuthError]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        try {
          // Configure API client with stored token first
          configureAuthToken(storedToken);
          // Verify token is valid by fetching user profile
          const response = await fetchUserProfile();
          setUser(response.data);
          setToken(storedToken);
          // Identify user in analytics
          analytics.identify(response.data);
          analytics.startSession();
        } catch (error) {
          // Token is invalid, clear it
          localStorage.removeItem('authToken');
          configureAuthToken(null);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await loginUser(username, password);
      const { access_token, user: userData } = response.data;
      
      // Store token in localStorage and configure API client immediately
      localStorage.setItem('authToken', access_token);
      configureAuthToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      // Track login event and identify user
      analytics.identify(userData);
      analytics.track('user_logged_in', {
        method: 'password',
        role: userData.role,
        is_alpha_tester: userData.is_alpha_tester || false,
      });
      analytics.startSession();
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (username, email, firstName, lastName, password, role = 'STUDENT', referralToken = null) => {
    try {
      const response = await registerUser(username, email, firstName, lastName, password, role, referralToken);
      const { access_token, user: userData } = response.data;
      
      // Store token in localStorage and configure API client immediately
      localStorage.setItem('authToken', access_token);
      configureAuthToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      // Track registration event and identify user
      analytics.identify(userData);
      analytics.track('user_registered', {
        role: userData.role,
        email_verified: userData.email_verified,
      });
      
      // Track alpha tester registration if applicable
      if (userData.is_alpha_tester) {
        const referrerId = sessionStorage.getItem('referrerId');
        analytics.trackAlphaTesterRegistered(
          referralToken || null,
          referrerId ? parseInt(referrerId) : null,
          {
            is_email_verified: userData.email_verified,
            registration_source: referralToken ? 'referral_landing' : 'register_page'
          }
        );
        
        // Clear referrer ID from session
        sessionStorage.removeItem('referrerId');
      }
      
      analytics.startSession();
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async (redirectToLogin = true) => {
    try {
      await logoutUser();
    } catch (error) {
      // Continue with logout even if server call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Track logout and end session
      analytics.track('user_logged_out');
      analytics.endSession();
      analytics.reset();
      
      // Clear local state and API client configuration
      localStorage.removeItem('authToken');
      configureAuthToken(null);
      setToken(null);
      setUser(null);
      
      // Redirect to login if requested
      if (redirectToLogin) {
        navigate('/login');
      }
    }
  }, [navigate]);

  const isAuthenticated = () => {
    return !!(token && user);
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    
    const roleHierarchy = {
      'STUDENT': 1,
      'TUTOR': 2,
      'ADMIN': 3
    };
    
    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 999;
    
    return userRoleLevel >= requiredRoleLevel;
  };

  const needsFirstLogin = () => {
    return user && user.first_login_completed === false;
  };

  const needsEmailVerification = () => {
    return user && user.role === 'STUDENT' && user.email_verified === false;
  };

  const handleFirstLogin = async (token, newPassword) => {
    try {
      let response;
      
      // If token is provided, use token-based endpoint (from email link)
      if (token) {
        response = await completeFirstLogin(token, newPassword);
      } else {
        // If no token, user is authenticated - use authenticated endpoint
        response = await completeFirstLoginAuthenticated(newPassword);
      }
      
      const { access_token, user: userData } = response.data;
      
      // Update stored token, configure API client, and update state
      localStorage.setItem('authToken', access_token);
      configureAuthToken(access_token);
      setToken(access_token);
      setUser(userData);
      
      // Track first login completion
      analytics.track('first_login_completed', {
        had_token: !!token,
      });
      
      // Track alpha tester onboarding if applicable
      if (userData.is_alpha_tester) {
        const registrationDate = new Date(userData.created_at);
        const daysSinceRegistration = Math.floor((new Date() - registrationDate) / (1000 * 60 * 60 * 24));
        
        analytics.trackAlphaTesterOnboarded(
          daysSinceRegistration,
          true, // first_login
          userData.trial_exams_used || 0
        );
      }
      
      // Re-identify with updated user data
      analytics.identify(userData);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Password change failed';
      return { success: false, error: message };
    }
  };

  const setAuthData = (access_token, userData) => {
    // Store token in localStorage and configure API client immediately
    localStorage.setItem('authToken', access_token);
    configureAuthToken(access_token);
    setToken(access_token);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated,
    hasRole,
    needsFirstLogin,
    needsEmailVerification,
    handleFirstLogin,
    setAuthData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export wrapper component that needs to be inside Router
export const AuthProvider = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};